import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Trophy, Activity, Camera, Users, Package, CheckCircle, Calendar } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip } from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/components/AuthContext';
import { analyticsService } from './analyticsService';
import { RecommendationItem } from './types';
import { cn } from '@/shared/lib/utils';

interface CreditScoreData {
  total_score: number;
  photo_consistency_score: number;
  activity_logging_score: number;
  yield_accuracy_score: number;
  network_engagement_score: number;
  verification_compliance_score: number;
  engagement_quality_score: number;
  current_streak_days: number;
  percentile_rank: number | null;
  last_calculated_at: string;
}

export const FarmerDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [scoreData, setScoreData] = useState<CreditScoreData | null>(null);
  const [recommendations, setRecommendations] = useState<RecommendationItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    fetchData();
  }, [user?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [scoreResult, recsData] = await Promise.all([
        supabase
          .from('credit_scores')
          .select('total_score, photo_consistency_score, activity_logging_score, yield_accuracy_score, network_engagement_score, verification_compliance_score, engagement_quality_score, current_streak_days, percentile_rank, last_calculated_at')
          .eq('user_id', user!.id)
          .maybeSingle(),
        analyticsService.fetchRecommendations(),
      ]);

      setScoreData(scoreResult.data);
      setRecommendations(recsData.slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!scoreData) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">{t('analytics.dashboard.noData', 'No Score Data Yet')}</h3>
          <p className="text-sm text-muted-foreground">{t('analytics.dashboard.noDataDesc', 'Log more activities to see your dashboard insights.')}</p>
        </CardContent>
      </Card>
    );
  }

  const radarData = [
    { factor: 'Photos', value: scoreData.photo_consistency_score, fullMark: 100 },
    { factor: 'Activity', value: scoreData.activity_logging_score, fullMark: 100 },
    { factor: 'Yield', value: scoreData.yield_accuracy_score, fullMark: 100 },
    { factor: 'Network', value: scoreData.network_engagement_score, fullMark: 100 },
    { factor: 'Verification', value: scoreData.verification_compliance_score, fullMark: 100 },
    { factor: 'Engagement', value: scoreData.engagement_quality_score, fullMark: 100 },
  ];

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Trophy className="h-5 w-5 text-primary" />
              <span className="text-xs text-muted-foreground">{t('analytics.dashboard.creditScore', 'Credit Score')}</span>
            </div>
            <div className="text-3xl font-bold text-foreground">{scoreData.total_score}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="h-5 w-5 text-primary" />
              <span className="text-xs text-muted-foreground">{t('analytics.dashboard.streak', 'Streak')}</span>
            </div>
            <div className="text-3xl font-bold text-foreground">{scoreData.current_streak_days}d</div>
            {scoreData.percentile_rank != null && (
              <div className="text-xs text-muted-foreground mt-1">Top {(100 - scoreData.percentile_rank).toFixed(0)}%</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Radar Chart */}
      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.dashboard.scoreFactors', 'Score Factors')}</CardTitle>
          <CardDescription>{t('analytics.dashboard.scoreFactorsDesc', 'Your credit score component breakdown')}</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="hsl(var(--border))" />
              <PolarAngleAxis dataKey="factor" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} stroke="hsl(var(--muted-foreground))" fontSize={10} />
              <Radar name="Score" dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.3} />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                }}
              />
            </RadarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Recommendations */}
      <div className="space-y-3">
        {recommendations.map((rec) => {
          const IconComponent =
            rec.category.toLowerCase().includes('network') ? Users :
            rec.category.toLowerCase().includes('photo') ? Camera :
            rec.category.toLowerCase().includes('harvest') ? Package :
            Activity;

          const priorityColor =
            rec.priority === 'high' ? 'bg-destructive' :
            rec.priority === 'medium' ? 'bg-yellow-500' :
            'bg-green-500';

          return (
            <div
              key={rec.id}
              className="relative p-4 rounded-2xl border bg-card shadow-sm"
            >
              <div className={cn("absolute top-4 left-4 w-2 h-2 rounded-full", priorityColor, rec.priority === 'high' && "animate-pulse")} />
              <div className="flex items-center gap-4 ml-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <IconComponent className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground">{rec.title}</div>
                  <div className="text-xs text-muted-foreground">{rec.description}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
